import React, { useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Breadcrumbs,
  Link,
  TextField,
  Snackbar,
} from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { useProduct, useInventoryByProduct } from '../../hooks/useProducts';
import { useCartStore } from '../../stores/cartStore';
import { ProductImageGallery } from '../../components/ProductImageGallery';
import { ProductImage } from '../../types/product';

export const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: product, isLoading: productLoading, error: productError } = useProduct(id!);
  const { data: inventory, isLoading: inventoryLoading } = useInventoryByProduct(id!);

  const [quantity, setQuantity] = useState(1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const addItem = useCartStore((state) => state.addItem);

  const handleAddToCart = () => {
    if (!product) return;

    // Check stock availability (public inventory DTO exposes `available` directly)
    const availableStock = inventory?.available ?? 0;

    if (availableStock < quantity) {
      alert(`Only ${availableStock} units available in stock`);
      return;
    }

    const price = product.price
      ? (typeof product.price === 'string' ? parseFloat(product.price) : product.price)
      : 0;

    addItem({
      productId: product.id,
      name: product.name,
      productAttributes: product.attributes || undefined,
      price,
      locationId: product.locationId,
      locationName: product.location?.name || 'Unknown Store',
      image: product.images?.[0]?.thumbnail,
      organizationId: product.organizationId,
      quantity,
    });

    setSnackbarOpen(true);
  };

  if (productLoading || inventoryLoading) {
    return (
      <Container maxWidth="lg" component="main">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (productError || !product) {
    return (
      <Container maxWidth="lg" component="main">
        <Alert severity="error">Product not found or failed to load.</Alert>
      </Container>
    );
  }

  const productPrice = product.price
    ? (typeof product.price === 'string' ? parseFloat(product.price) : product.price)
    : 0;

  // Stock information — use the public inventory DTO's own computed fields
  // (available / inStock / lowStock). Raw quantity/reserved are intentionally
  // not exposed to the storefront, so we never recompute stock client-side.
  const availableStock = inventory?.available ?? 0;
  const isOutOfStock = !inventory?.inStock;
  const isLowStock = inventory?.lowStock ?? (availableStock > 0 && availableStock <= 10);

  return (
    <Container maxWidth="lg" component="main">
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/" underline="hover" color="inherit">
          Home
        </Link>
        <Link component={RouterLink} to="/products" underline="hover" color="inherit">
          Products
        </Link>
        <Typography color="text.primary">{product.name}</Typography>
      </Breadcrumbs>

      <Grid container spacing={4}>
        {/* Product Image Gallery */}
        <Grid item xs={12} md={6}>
          <ProductImageGallery
            images={Array.isArray(product.images) && product.images.length > 0 && typeof product.images[0] === 'object'
              ? (product.images as unknown as ProductImage[])
              : []
            }
            productName={product.name}
          />
        </Grid>

        {/* Product Info */}
        <Grid item xs={12} md={6}>
          <Typography variant="h3" gutterBottom fontWeight="bold">
            {product.name}
          </Typography>

          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {product.category && <Chip label={product.category} color="primary" />}
            {product.brand && <Chip label={product.brand} variant="outlined" />}
            {product.isActive && <Chip label="Available" color="success" />}
            {product.sku && <Chip label={`SKU: ${product.sku}`} variant="outlined" size="small" />}
          </Box>

          {product.location && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Sold by:{' '}
              <Link component={RouterLink} to={`/stores/${product.locationId}`}>
                {product.location.name}
              </Link>
            </Typography>
          )}

          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" color="primary" fontWeight="bold">
              {productPrice.toFixed(2)} DZD
            </Typography>
          </Box>

          <Typography variant="body1" paragraph>
            {product.description || 'No description available'}
          </Typography>

          {/* Product Attributes */}
          {product.attributes && Object.keys(product.attributes).length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Attributes:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {Object.entries(product.attributes).map(([key, value]) => (
                  <Chip
                    key={key}
                    label={`${key}: ${value}`}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Stock Availability */}
          <Box sx={{ mb: 3 }}>
            {isOutOfStock ? (
              <Alert severity="error">
                Out of Stock - This item is currently unavailable
              </Alert>
            ) : isLowStock ? (
              <Alert severity="warning">
                Only {availableStock} left in stock - Order soon!
              </Alert>
            ) : (
              <Alert severity="success">
                In Stock
              </Alert>
            )}
          </Box>

          {/* Quantity */}
          <TextField
            type="number"
            label="Quantity"
            value={quantity}
            onChange={(e) => {
              const newQty = Math.max(1, parseInt(e.target.value) || 1);
              const maxQty = availableStock;
              setQuantity(Math.min(newQty, maxQty));
            }}
            InputProps={{
              inputProps: {
                min: 1,
                max: availableStock
              }
            }}
            fullWidth
            sx={{ mb: 3 }}
            disabled={isOutOfStock}
            helperText={isLowStock && availableStock > 0 ? `Max: ${availableStock}` : ''}
          />

          {/* Add to Cart Button */}
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<AddShoppingCartIcon />}
            onClick={handleAddToCart}
            disabled={isOutOfStock}
            sx={{ mb: 2 }}
          >
            {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
          </Button>

          {/* Product Tags */}
          {product.tags && product.tags.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Tags:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {product.tags.map((tag, index) => (
                  <Chip key={index} label={tag} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message="Product added to cart"
      />
    </Container>
  );
};
